import { BookingOrchestrator } from '../booking-orchestrator';
import {
  BookingContext,
  BookingParams,
  BookingPosition,
  BookingResult,
  BookingServices,
  SystemContext,
} from '../types';
import { Validation } from '../validation/validation';
import { ModelAdapters } from '../interface/model-adapters';
import { BookingClientIds } from '../booking-client-ids';
import { BookingReference } from '../util/booking-reference';

/**
 * Fortschrittssignal, das `BookingRequest.submit()` pro abgeschlossener
 * Position an den optional registrierten Callback liefert.
 */
export interface BookingProgress {
  /** 1-basierter Index der gerade abgeschlossenen Position. */
  current: number;
  /** Gesamtanzahl der zu buchenden Positionen. */
  total: number;
  /** Ergebnis der zuletzt verarbeiteten Position (Success oder Failure). */
  lastResult: BookingResult;
}

/**
 * Callback-Signatur für `BookingRequest.onProgress()`. Wird nach jeder
 * verarbeiteten Position aufgerufen, ehe die nächste startet.
 */
export type BookingProgressCallback = (progress: BookingProgress) => void;

/**
 * Vierte und letzte Builder-Stage mit den Endpunkten `validate()` und
 * `submit()`. Beide sind getrennt, damit der Aufrufer dazwischen eine
 * User-Confirmation einholen kann; `submit()` verlangt die `Validation` als
 * Evidence.
 */
export class BookingRequest {
  private progressCallback?: BookingProgressCallback;

  /**
   * Stabile `client_id` pro Position, gebunden an diese Request-Instanz: ein
   * erneuter `submit()` (Retry) sendet dieselben IDs, das Backend dedupliziert.
   * Eine fachlich neue Buchung verlangt einen neuen Request über den Builder.
   */
  private readonly clientIds = new BookingClientIds();

  /**
   * Referenz aller Movements dieser Buchung: aus dem Kontext übernommen oder
   * als Ad-hoc-Referenz erzeugt. Einmal pro Request, damit ein Retry dieselbe
   * Referenz sendet und die Movements gruppierbar bleiben.
   */
  private readonly bookingReference: string;

  /**
   * @param orchestrator Kernel, das die einzelnen Position-Buchungen ausführt
   * @param systemContext System-weite Konfiguration aus den Funktionsschaltern
   * @param bookingContext Vollständig gesammelter Kontext (Quelle, Ziel, Intent)
   * @param services Von der App injizierte Model- und Warehouse-Services
   * @param positions Liste der zu buchenden Positionen
   */
  constructor(
    private readonly orchestrator: BookingOrchestrator,
    private readonly systemContext: SystemContext,
    private readonly bookingContext: Partial<BookingContext>,
    private readonly services: BookingServices,
    private readonly positions: BookingPosition[],
  ) {
    this.bookingReference =
      bookingContext.bookingReference ?? BookingReference.forTransfer();
  }

  /**
   * Registriert einen Callback, der nach jeder verarbeiteten Position
   * aufgerufen wird.
   *
   * @param cb Fortschritts-Callback, das je Position ein `BookingProgress` erhält
   * @returns Diese Request-Instanz für Method-Chaining
   */
  public onProgress(cb: BookingProgressCallback): this {
    this.progressCallback = cb;
    return this;
  }

  /**
   * Validiert alle Positionen über den Orchestrator und sammelt die Ergebnisse
   * in einer `Validation`. Ohne Positionen wird eine leere (constraint-freie)
   * Validation zurückgegeben. Das Ergebnis dient als Evidence für `submit()`.
   *
   * @returns Gesammelte Validierung über alle Positionen
   */
  public async validate(): Promise<Validation> {
    const validation = new Validation();
    if (this.positions.length < 1) {
      // Special Case: Wir müssen davon ausgehen, dass es mindestens eine
      // Position gibt.
      // → Umkehrschluss: Constraints werden gar nicht gecheckt, also aufpassen
      return validation;
    }
    const params = this.buildParams();
    for (const position of this.positions) {
      const summary = await this.orchestrator.validate(position, params);
      validation.addSummary(summary);
    }
    return validation;
  }

  /**
   * Bucht alle Positionen sequenziell, fail-fast beim ersten Failure (mit
   * Position-Index in der Message). Bereits gebuchte Positionen
   * bleiben ohne Rollback gebucht; eine Rückbuchung ist Sache des Aufrufers.
   *
   * @param evidence Zuvor via `validate()` erzeugte Validierung als Nachweis
   * @returns Gesamtergebnis der Buchung (Erfolg oder erstes Failure)
   */
  public async submit(evidence: Validation): Promise<BookingResult> {
    if (this.positions.length < 1) {
      return {
        success: false,
        message: 'Buchung muss mindestens eine Position haben',
      };
    }
    const params = this.buildParams();
    for (let i = 0; i < this.positions.length; i++) {
      const result = await this.orchestrator.book(
        this.positions[i],
        params,
        evidence,
      );
      this.progressCallback?.({
        current: i + 1,
        total: this.positions.length,
        lastResult: result,
      });
      if (!result.success) {
        return {
          success: false,
          message: `Position ${i + 1}: ${result.message}`,
          retryable: result.retryable,
        };
      }
    }
    return { success: true, message: '' };
  }

  private buildParams(): BookingParams {
    return {
      context: {
        // Cast is safe since we collected all required params (source, target, ...)
        booking: {
          ...this.bookingContext,
          bookingReference: this.bookingReference,
        } as BookingContext,
        system: this.systemContext,
      },
      adapters: new ModelAdapters(this.services),
      clientIds: this.clientIds,
    };
  }
}
